// Serializer: canvas graph → engine CreateWorkflowInput, with gap analysis. Unsupported options
// stay authorable and serialize best-effort as WorkflowGaps; the only hard block is "no trigger".
// `trigger` and `exit` are structural rather than engine nodes, so both are dropped from the node
// list and their edges pruned.
import type { Edge } from '@xyflow/react'
import { assigneeInput } from 'lib/taskAssignee'
import type {
  CreateWorkflowInput,
  WorkflowEdgeInput,
  WorkflowNodeInput,
  WorkflowTriggerFilterInput,
  WorkflowTriggerInput,
} from 'app/generated/workflows'
import { WorkflowNodeType, WorkflowReenrollmentPolicy, WorkflowTriggerEvent } from 'app/generated/workflows'
import { type Condition, conditionScope, type FieldOperator, operatorNeedsValue } from './conditions'
import { CONTACT_FIELD_KEYS, CONTACT_TAGS_FIELD, isBooleanFieldKey, PAPERWORK_STATUS_FIELD } from './dataContext'
import { forkHandles } from './autoLayout'
import { pendingEngineGaps } from './pendingEngineSupport'
import { chainEdges, chainNodeIds, type StepChain, splitIntoChecks } from './stepChecks'
import {
  type TriggerEngineEvent,
  triggerDef,
  triggerEffectiveSource,
  triggerObject,
  triggerSourceFilterable,
  type TriggerKind,
} from './triggerCatalog'
import { TREATMENT_MOMENT_EVENTS, treatmentFilters } from './treatmentTrigger'
import type { ReenrollmentPolicy, WaitUnit, WorkflowNode, WorkflowSettings } from 'app/types/workflows'
import type { ListCopy } from 'lib/listCopy'

export type WorkflowGapCode =
  | 'unsupported_node'
  | 'unsupported_trigger'
  | 'lossy_field'
  | 'trigger_mapping'
  | 'unsupported_settings'

export type WorkflowGap = {
  /** The node this gap belongs to, if any (trigger/settings gaps have none). */
  nodeId?: string
  code: WorkflowGapCode
  /** User-facing, "flagged for backend" phrasing. */
  message: string
}

export type SerializeResult = {
  /** The engine payload. `undefined` only when there's no trigger (see `blocked`). */
  input?: CreateWorkflowInput
  /** True only when a hard gap (no trigger) prevents create. Lossy fields do NOT block. */
  blocked: boolean
  gaps: WorkflowGap[]
}

const UNIT_SUFFIX: Record<WaitUnit, string> = { minutes: 'm', hours: 'h', days: 'd' }

/** Wait duration → engine string, e.g. (2, 'hours') → "2h". */
export function encodeDuration(value: number, unit: WaitUnit): string {
  return `${value}${UNIT_SUFFIX[unit]}`
}

// The builder's phone-field keys match the engine's SEND_SMS toField values exactly, including
// the form envelope's `phone` since api #566 (TRK-5579): the engine resolves it off the
// triggering Form record and throws loudly when the form carries no phone. Straight passthrough.
const encodeToField = (field?: string): string | undefined => field || undefined

// The builder's condition operators → the engine's trigger/branch filter operators.
const FIELD_OP_MAP: Record<FieldOperator, string> = {
  is: 'exactly',
  is_not: 'not_exactly',
  contains: 'contains',
  greater_than: 'greater_than',
  greater_equal_than: 'greater_equal_than',
  lesser_than: 'lesser_than',
  lesser_equal_than: 'lesser_equal_than',
  is_set: 'attribute_exists',
  is_empty: 'attribute_not_exists',
  more_than_days_ago: 'lesser_than',
  less_than_days_ago: 'greater_equal_than',
}

/** `attribute_exists` is the one operator the engine refuses to evaluate bare. Measured on dev
 *  2026-08-28 with CONTACT_UPDATED probe workflows over three contacts of known state: a bare
 *  `{ nearestAppointmentDate, attribute_exists }` state filter enrolled nobody AND recorded no
 *  decline records at all (so it is never evaluated, not evaluated-and-rejected); the identical
 *  filter with "true" enrolled exactly the two contacts that had the field and declined the third.
 *  Nobody types this value ("is set" carries no user value), so it is added on the way out
 *  and stripped on the way back in (deserialize.conditionFromFilter).
 *  `attribute_not_exists` is NOT affected: verified working bare the same way. */
export const ATTRIBUTE_EXISTS_VALUE = 'true'

/** Canvas source-handle → engine source-handle.
 *  - waitForReply: yes → replied, no → no_reply
 *  - waitUntil: yes → conditions_met, no → timed_out
 *  - createTask: task → task (its task path), main → done
 *  - branch: yes → the "yes" condition, no → default (unmatched)
 *  - filter: single output is the matched ("pass") path
 *  - waitForKeyword / waitForCallResult / linear: pass through, default "done" (a keyword wait's
 *    exits are its option ids, then other_reply and no_reply) */
export function encodeSourceHandle(sourceKind: WorkflowNode['type'] | undefined, handle?: string | null): string {
  switch (sourceKind) {
    case 'waitForReply':
      return handle === 'yes' ? 'replied' : 'no_reply'
    case 'waitUntil':
      return handle === 'yes' ? 'conditions_met' : 'timed_out'
    case 'branch':
      return handle === 'yes' ? 'yes' : 'default'
    case 'filter':
      return 'pass'
    default:
      return handle || 'done'
  }
}

/** The contact's own `tags`, which the builder keys as CONTACT_TAGS_FIELD (see dataContext). */
export const CONTACT_TAGS_ATTRIBUTE = `tags`

/** Map the builder's condition rows to engine filters (AND within a condition). Event predicates
 *  (e.g. "appointment booked") have no filter equivalent yet, so they're dropped and flagged. */
function toFilters(conditions: Condition[]): { filters: WorkflowTriggerFilterInput[]; droppedEvent: boolean } {
  const filters: WorkflowTriggerFilterInput[] = []
  let droppedEvent = false
  for (const condition of conditions) {
    if (condition.type === 'event') {
      droppedEvent = true
      continue
    }
    if (!condition.field) continue
    const daysAgo = condition.operator === 'more_than_days_ago' || condition.operator === 'less_than_days_ago'
    let value: string | undefined
    if (condition.operator === 'is_set') value = ATTRIBUTE_EXISTS_VALUE
    else if (daysAgo) value = `$now-${condition.value?.trim()}d`
    else if (operatorNeedsValue(condition.operator)) value = condition.value
    filters.push({
      attribute: condition.field === CONTACT_TAGS_FIELD ? CONTACT_TAGS_ATTRIBUTE : condition.field,
      operator: FIELD_OP_MAP[condition.operator],
      value,
    })
    // A contact who has never texted has no date, and a date comparison never matches a missing
    // value. "Hasn't texted in N days" includes them, so the comparison gets an OR twin.
    // splitIntoChecks always puts this condition in an OR check.
    if (condition.operator === 'more_than_days_ago') {
      filters.push({ attribute: condition.field, operator: 'attribute_not_exists' })
    }
  }
  return { filters, droppedEvent }
}

const REENROLLMENT_MAP: Record<ReenrollmentPolicy, WorkflowReenrollmentPolicy> = {
  ignore: WorkflowReenrollmentPolicy.Ignore,
  restart: WorkflowReenrollmentPolicy.Restart,
  allow_multiple: WorkflowReenrollmentPolicy.AllowMultiple,
}

const ENGINE_EVENT_MAP: Record<TriggerEngineEvent, WorkflowTriggerEvent> = {
  contact_created: WorkflowTriggerEvent.ContactCreated,
  contact_updated: WorkflowTriggerEvent.ContactUpdated,
  call_created: WorkflowTriggerEvent.CallCreated,
  call_updated: WorkflowTriggerEvent.CallUpdated,
  call_finished_processing: WorkflowTriggerEvent.CallFinishedProcessing,
  appointment_date_reached: WorkflowTriggerEvent.AppointmentDateReached,
  contact_date_reached: WorkflowTriggerEvent.ContactDateReached,
  form_created: WorkflowTriggerEvent.FormCreated,
  form_updated: WorkflowTriggerEvent.FormUpdated,
  activity_created: WorkflowTriggerEvent.ActivityCreated,
  activity_updated: WorkflowTriggerEvent.ActivityUpdated,
  message_created: WorkflowTriggerEvent.MessageCreated,
  task_created: WorkflowTriggerEvent.TaskCreated,
  task_updated: WorkflowTriggerEvent.TaskUpdated,
  appointment_created: WorkflowTriggerEvent.AppointmentCreated,
  appointment_updated: WorkflowTriggerEvent.AppointmentUpdated,
  treatment_plan_updated: WorkflowTriggerEvent.TreatmentPlanUpdated,
  paperwork_completed: WorkflowTriggerEvent.PaperworkCompleted,
}

/** Activity types that never emit generic Activity events — they're first-class Event sources of
 *  their own (Call/Form) or the change log itself (Change). An activity trigger narrowed to one of
 *  these would silently never fire (PR #371 excludes them at the Activity.afterSave emission). */
const ACTIVITY_EVENT_EXCLUDED_TYPES = [`Call`, `Form`, `Change`]

/** Our message field keys are frontend aliases (`direction`/`channel` were taken by Call/Activity
 *  when we contracted them — see MESSAGE_FIELDS). Message events record the raw Message fields and
 *  the event path has no server-side alias table (unlike the old poller), so an unmapped
 *  `data.messageDirection.to` query would silently never match. Map at the serialization boundary.
 *  Applies to state-filters too: loadSourceRecord returns the raw Message record. */
export const MESSAGE_ENGINE_ATTRIBUTE: Record<string, string> = {
  messageDirection: `direction`,
  messageChannel: `channel`,
  messageText: `text`,
  fromNumber: `from`,
  toNumber: `to`,
}

function toMessageAttributes(filters: WorkflowTriggerFilterInput[]): WorkflowTriggerFilterInput[] {
  return filters.map((filter) => ({
    ...filter,
    attribute: MESSAGE_ENGINE_ATTRIBUTE[filter.attribute] ?? filter.attribute,
  }))
}

/** Pipeline membership is `pipelines: [{id, stage, key}]` with `key` = `<pipelineId>::<stage>`
 *  (TRK-5495). Only `pipelines.key` works in BOTH a trigger and a step (measured on dev 08-14), so
 *  the picker writes that; a bare stage name is a pre-change save, still fine in a step and still
 *  impossible in a trigger. The builder's `pipelineStage` matches no engine field, so it always
 *  needs translating. */
export const STAGE_KEY_SEPARATOR = `::`
export const isPipelineStageKey = (value?: string): boolean => Boolean(value?.includes(STAGE_KEY_SEPARATOR))

/** The step-filter translation for a stage condition, BOTH ways, in one place because the two halves
 *  have to agree exactly: deserialize (which reopens an engine-only workflow) reverses what this
 *  emits, and a disagreement would silently rewrite a running workflow's stored attribute.
 *
 *  Forward, the value picks the attribute. Backwards it is not a plain inverse — one builder field
 *  produces two engine attributes — so a pair that disagrees (a bare stage under `pipelines.key`,
 *  which the picker never writes) keeps the raw attribute rather than being translated into one that
 *  re-serializes differently. Anything that isn't a stage attribute passes straight through. */
export function stepStageAttribute(attribute: string, value?: string): string {
  const forward = isPipelineStageKey(value) ? `pipelines.key` : `pipelines.stage`
  if (attribute === `pipelineStage`) return forward
  return attribute === forward ? `pipelineStage` : attribute
}

/** A step filter's value, as the DESTINATION compares it. Branch/Filter conditions are JSON the
 *  engine parses and compares with `===` against a live record, so a boolean field needs a real
 *  boolean: "true" would never equal `true` and the group would silently never match. Trigger
 *  filters keep strings — those go to Elasticsearch, which coerces. */
function toStepValue(attribute: string, value?: string | null): string | boolean | undefined {
  if (isBooleanFieldKey(attribute)) return value === `true`
  return value ?? undefined
}

/** Branch/Filter filters, ready for the conditions JSON. */
function toStepFilters(filters: WorkflowTriggerFilterInput[]) {
  return toStepAttributes(filters).map((filter) => ({
    ...filter,
    value: toStepValue(filter.attribute, filter.value),
  }))
}

function toStepAttributes(filters: WorkflowTriggerFilterInput[]): WorkflowTriggerFilterInput[] {
  // Only the builder's own field is translated on the way out: an engine attribute already in a
  // saved condition (a doc reopened before deserialize could read it back) stays as it is.
  return filters.map((filter) =>
    filter.attribute === `pipelineStage`
      ? { ...filter, attribute: stepStageAttribute(filter.attribute, filter.value ?? undefined) }
      : filter,
  )
}

/** Trigger-side, only the composite key resolves (the trigger matcher does a flat lookup on the
 *  event's changed attributes, and `pipelines.key` is emitted there as its own field). */
function toTriggerStageAttributes(filters: WorkflowTriggerFilterInput[]): WorkflowTriggerFilterInput[] {
  return filters.map((filter) =>
    filter.attribute === `pipelineStage` && isPipelineStageKey(filter.value ?? undefined)
      ? { ...filter, attribute: `pipelines.key` }
      : filter,
  )
}

/** Contact fields nothing can compare, because a contact stores ONE name and these are split out of
 *  it for display. Measured 08-14 by branching on each field's own live value: name, cellPhone,
 *  leadStatus, lifecycleStage, patientStatus and locationId all matched; these two took the default
 *  path every time. That is the data model, not a bug to fix, so the condition pickers no longer
 *  offer them (see `matchable` in dataContext). This stays as the safety net for a condition saved
 *  before that, and for anything that reaches the serializer another way — silently sending every
 *  contact down the "no" path is the one outcome worth blocking Activate over. */
const UNMATCHABLE_STEP_FIELDS: Record<string, string> = {
  firstName: `First name`,
  lastName: `Last name`,
}

// Special-character filter values (hyphens, colons, UUIDs) are safe since PR #403 removed the
// engine's sanitizeValue from the filter path — verified live 08-06 (TRK-5421): the exact
// hyphenated-formId repro enrolled, including Monday's originally-unmatched event.

/** The manual-enrollment convention (TRK-5430, verified live 08-12): the schema requires a trigger,
 *  so "manual only" is a CONTACT_UPDATED trigger with this filter, which no contact ever matches.
 *  The evaluator skips it; direct enrollment (Runs panel) and bulk enrollment push past it, and the
 *  re-enrollment policy still applies. The value is deliberately self-describing for anyone reading
 *  the raw workflow document. */
export const MANUAL_ENROLLMENT_FILTER: WorkflowTriggerFilterInput = {
  attribute: 'leadStatus',
  operator: 'exactly',
  value: 'manual-enrollment-only',
}

/** Whether a stored engine trigger is the manual-enrollment convention. Shared by the deserializer
 *  (round-trip back to the `manual` kind) and the contacts bulk action (which workflows to offer
 *  for "Enroll in workflow"). */
export function isManualEnrollmentTrigger(trigger: {
  filters?: { attribute: string; value?: string | null }[] | null
}): boolean {
  return (trigger.filters ?? []).some(
    (f) => f.attribute === MANUAL_ENROLLMENT_FILTER.attribute && f.value === MANUAL_ENROLLMENT_FILTER.value,
  )
}

function serializeTrigger(
  node: Extract<WorkflowNode, { type: 'trigger' }>,
  /** Required, not defaulted: a silent fallback to the pipeline wording would hide the one caller
   *  that forgot to pass its real toggle state. Every caller must say explicitly which wording it means. */
  copy: ListCopy,
): {
  trigger: WorkflowTriggerInput
  gaps: WorkflowGap[]
} {
  const data = node.data
  const def = triggerDef(data.kind)
  const gaps: WorkflowGap[] = []

  // Manual enrollment has no event, conditions, or source — only the convention filter and the
  // re-enrollment policy (which the engine applies to pushed enrollments too).
  if (data.kind === 'manual') {
    return {
      trigger: {
        event: WorkflowTriggerEvent.ContactUpdated,
        filters: [MANUAL_ENROLLMENT_FILTER],
        reenrollment: REENROLLMENT_MAP[data.reenrollment],
        filterLogic: 'and',
        requireContactMatch: true,
      },
      gaps,
    }
  }
  // The trigger's preset filters (e.g. a Call event → type=Call) come first, then any user-chosen
  // activity-type narrowing on the generic activity triggers.
  const filters: WorkflowTriggerFilterInput[] = [...(def.presetFilters ?? []), ...treatmentFilters(data.treatment)]

  if (def.configurableActivityType && data.activityType) {
    filters.push({ attribute: 'type', operator: 'exactly', value: data.activityType })
    // Call/Form/Change activities are excluded from generic Activity events (they have their own
    // sources), so this narrowing would silently never fire — point at the first-class trigger.
    if (ACTIVITY_EVENT_EXCLUDED_TYPES.includes(data.activityType)) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `${data.activityType} activities don't emit generic Activity events. Use the dedicated ${
          data.activityType === 'Change' ? 'Any contact change' : data.activityType
        } trigger instead.`,
      })
    }
  }
  // User entry conditions split by scope (PR #373): "just changed" rows become change-filters
  // (queried against the event's data), "currently" rows become state-filters (checked against the
  // live record at enrollment time).
  const stateFilters: WorkflowTriggerFilterInput[] = []
  if (data.conditions?.length) {
    const changeConditions = data.conditions.filter((c) => conditionScope(c) === 'change')
    const stateConditions = data.conditions.filter((c) => c.type === 'field' && conditionScope(c) === 'state')
    const { filters: entryFilters, droppedEvent } = toFilters(changeConditions)
    filters.push(...entryFilters)
    stateFilters.push(...toFilters(stateConditions).filters)
    // Both groups read the SOURCE record only: change filters its event, state filters the live record
    // (loadSourceRecord). On any other trigger a CONTACT field silently never matches.
    if (
      def.object !== 'contact' &&
      data.conditions.some((c) => c.type === 'field' && c.field && CONTACT_FIELD_KEYS.includes(c.field))
    ) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `A trigger condition on a contact field isn't checked here: the engine only sees the ${def.objectLabel.toLowerCase()} itself, so the workflow would silently never enroll. Use a Filter step after the trigger instead.`,
      })
    }
    if (droppedEvent) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message:
          'Trigger: event entry-conditions (e.g. "appointment booked") aren\'t usable as enrollment filters yet.',
      })
    }
    // Which call fields can be matched as "just changed" depends on when they are written: the
    // outcome records its assignment on the post-transcription update, but `answered` and
    // `direction` are set at create and never touched again, so "just changed" on those only works
    // on Any call — on Call analyzed they would wait forever. "Currently" works on both.
    if (def.object === 'call') {
      const changeFields = data.conditions
        .filter((c) => c.type === 'field' && conditionScope(c) === 'change')
        .map((c) => (c.type === 'field' ? c.field : undefined))
      const createOnlyFields = ['answered', 'direction'].filter((field) => changeFields.includes(field))
      if (createOnlyFields.length && data.kind === 'call_updated') {
        gaps.push({
          nodeId: node.id,
          code: 'unsupported_trigger',
          message: `A call's ${createOnlyFields.join(' and ')} ${createOnlyFields.length > 1 ? 'are' : 'is'} recorded when the call arrives and never changes afterwards, so "just changed" can't match on a Call analyzed trigger. Use the Any call trigger, or change this to a "currently" check.`,
        })
      }
    }
  }
  // Form scoping rides the generic change-filter contract post-pivot: form_created events carry the
  // whole Form record, so `data.formId.to` matches the form-DEFINITION id(s). Forms discovered before
  // formIds were captured (or whose record has an empty formId — legacy imports) can't be scoped;
  // those keep the honest gap instead of silently firing for every form (TRK-5366).
  if (data.formScope?.length) {
    const scopedIds = [...new Set(data.formScope.flatMap((form) => form.formIds ?? []))]
    const unscopable = data.formScope.filter((form) => !form.formIds?.length)
    if (scopedIds.length) {
      filters.push(
        scopedIds.length > 1
          ? { attribute: 'formId', operator: 'in', value: JSON.stringify(scopedIds) }
          : { attribute: 'formId', operator: 'exactly', value: scopedIds[0] },
      )
    }
    if (unscopable.length) {
      const names = unscopable.map((form) => form.title).join(', ')
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `${names}: this form has no form id on record, so the trigger can't scope to it. Re-pick it from the form list (older selections predate id capture), or remove it to fire for the scoped forms only.`,
      })
    }
  }
  // requireContactMatch is enforced by the engine — verified on dev 2026-07-29 (TRK-5369): a matched
  // activity enrolled while an unmatched one from the same evaluation window was skipped.
  // Enrolling on a pipeline stage is live since TRK-5495 (verified on dev 08-14: a stage move
  // enrolled through a `pipelines.key` filter). It needs the pipeline as well as the stage, which is
  // what the stage picker now writes. A stage saved before that change carries only a name, and a
  // name alone still cannot enroll, so that one keeps its gate.
  const legacyStage = data.conditions?.find(
    (condition) =>
      condition.type === 'field' && condition.field === 'pipelineStage' && !isPipelineStageKey(condition.value),
  )
  if (legacyStage) {
    gaps.push({
      nodeId: node.id,
      code: 'unsupported_trigger',
      message: copy.stageConditionPredatesLists,
    })
  }
  // System-sourced contact saves record only a whitelist of change fields (leadOwner, leadStatus,
  // lifecycleStage, channel, closedLostReason, isQualified, patientStatus, pipelines), and `*Id`
  // fields never appear in change data at all (verified live 08-10). So a "just changed" condition
  // on those silently never matches the PMS syncs users mean, while "currently" reads the live
  // contact and works — only change-scope conditions gate.
  const UNRECORDED_CHANGE_FIELDS: Record<string, string> = {
    appointmentStatus:
      'PMS updates to appointment status never reach the contact\'s event stream. Trigger on "Any appointment change" instead (that path is live), or use a "currently" condition.',
    locationId:
      'Location changes aren\'t recorded in the event stream, so this can never fire. A "currently" location condition works.',
  }
  for (const condition of data.conditions ?? []) {
    if (condition.type !== 'field' || !condition.field) continue
    const message = UNRECORDED_CHANGE_FIELDS[condition.field]
    // Date-anchored triggers route every condition to state filters (the engine re-reads the record
    // at due time), so "this change is never recorded" cannot apply to them.
    if (message && conditionScope(condition) === 'change' && !isDateAnchored(data.kind)) {
      gaps.push({ nodeId: node.id, code: 'unsupported_trigger', message })
    }
  }
  // Change-events the engine can't emit yet (tag added, stage/field changed, appointment cancelled,
  // missed call) stay authorable but block Activate, so no one ships a workflow that silently never fires.
  if (def.status === 'backend') {
    gaps.push({
      nodeId: node.id,
      code: 'unsupported_trigger',
      message: `The “${def.label}” trigger needs ${def.backendNote ?? 'backend support'} before it can go live. Flagged for backend.`,
    })
  }

  if (def.object === 'treatment_plan') {
    if (!data.treatment?.milestone) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: 'Choose what happened to the treatment plan.',
      })
    }
    // The treatment choices and the user's change rows share the engine's one change-filter list and
    // one filterLogic, so "match any" over more than one entry would OR a choice away and enroll on
    // every plan change.
    if (data.match === 'any' && filters.length > 1) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message:
          '"Match any" can\'t combine with the treatment choices: the engine keeps the treatment choices and your changes in one list and would enroll on every plan change. Use "match all", or move the either-or check into a Filter step after the trigger.',
      })
    }
  }

  // "Triggered by: Staff / System" narrows enrollment to human vs machine changes (the Contact
  // chokepoint fires on both). LIVE since the pivot: every Event records its source, and the engine
  // compiles a changeSource filter to hand-built terms (source:user* / source:System), bypassing the
  // TRK-5421 sanitizer. VERIFIED on dev 2026-08-05 with paired rigs: staff-only enrolled exactly the
  // "user Patrick" edit; system-only exactly the "System" form-ingestion recompute; no cross-fire.
  // Note contact_updated DEFAULTS to 'user' (catalog) — that default now actually enforces, which is
  // the intended robot-storm/self-feed fence.
  const source = triggerEffectiveSource(data.kind, data.source)
  if (triggerSourceFilterable(data.kind) && source !== 'any') {
    filters.push({ attribute: 'changeSource', operator: 'exactly', value: source })
    // The engine ORs the ENTIRE filters array under filterLogic 'or' — "staff-only AND (a OR b)"
    // would become "staff OR a OR b". Block that combo rather than silently widen enrollment.
    if (data.match === 'any' && filters.length > 1) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `"Match any" can't combine with a Triggered-by filter here: the engine keeps trigger conditions in one list, so it would OR the ${source === 'user' ? 'staff' : 'system'} filter in too and enroll on every such change. Either use "match all", set Triggered by to Anyone, or move the either-or check into a Filter step right after the trigger, where "match any" works.`,
      })
    }
  }

  // Values that look boolean or numeric ("false", "42") used to be coerced on save and could never
  // match the string an event stores, which is what silently stopped the missed-call template. Fixed
  // by trackable-api #503 and QA'd on dev 08-18: "false" now stores as "false", a Contact updated
  // workflow filtering isQualified "false" enrolled and completed on the change, and its "true" twin
  // stayed at zero runs. Array operators still parse. Gate removed; the note stays so it isn't
  // re-derived from scratch the next time a filter mysteriously matches nothing.

  // Change filters read the event's own data, where the stage arrives as `pipelines.key`. State
  // filters read the live record, which the engine walks by dot-path, so they take the same
  // translation as a step's.
  const engineFilters = def.object === 'message' ? toMessageAttributes(filters) : toTriggerStageAttributes(filters)
  const engineStateFilters =
    def.object === 'message' ? toMessageAttributes(stateFilters) : toStepAttributes(stateFilters)

  // Date-anchored triggers compile differently: the filters ARE the schedule. One `date_reached`
  // filter per moment, ORed, because each offset fires independently for the same record
  // (trackable-api #524) — which is what makes a five-step ladder one workflow instead of five.
  //
  // That OR is why the user's own conditions become STATE filters here rather than change filters:
  // the engine keeps one filterLogic for the whole change-filter list, so ANDing "status is
  // Scheduled" alongside ORed offsets is not expressible. State filters read the live record at due
  // time and carry their own logic, which is also the more correct question: it asks whether the
  // appointment is STILL scheduled when the reminder is about to send.
  if (isDateAnchored(data.kind)) {
    const offsets = data.dateOffsets ?? []
    const dateField = data.dateField
    // The API rejects any other policy outright: "Scheduled date triggers require reenrollment to be
    // ignore" (08-20). Caught here so the canvas says so before Save does.
    if (data.reenrollment !== 'ignore') {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `A date trigger runs its schedule once per record, so re-enrollment has to be "Don't run it again". Each moment you add already fires on its own.`,
      })
    }
    if (!dateField) {
      gaps.push({ nodeId: node.id, code: 'unsupported_trigger', message: `Choose which date this counts from.` })
    }
    // Schedule values deserialize could not render (see decodeOffset). They pass through verbatim
    // rather than being re-encoded, so saving can never silently delete or reschedule a send the
    // engine is still running, and they block Activate until someone repairs them.
    const invalid = data.invalidDateOffsets ?? []
    if (!offsets.length && !invalid.length) {
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message: `Add at least one moment, like "3 days before". A date trigger with no offsets would never fire.`,
      })
    }
    if (invalid.length) {
      const quoted = invalid.map((value) => `“${value || `empty`}”`).join(`, `)
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_trigger',
        message:
          invalid.length === 1
            ? `This trigger has a saved moment this builder can't read (${quoted}). It is kept exactly as saved, so nothing is lost, but replace it with a moment from the list before activating.`
            : `This trigger has ${invalid.length} saved moments this builder can't read (${quoted}). They are kept exactly as saved, so nothing is lost, but replace them with moments from the list before activating.`,
      })
    }
    const dateFilters = [
      ...offsets.map((offset) => ({
        attribute: dateField ?? ``,
        operator: 'date_reached',
        value: encodeOffset(offset),
      })),
      ...invalid.map((value) => ({ attribute: dateField ?? ``, operator: 'date_reached', value })),
    ]
    // The user's entry conditions ride along as state filters (see above). Change filters are the
    // schedule and nothing else.
    const combinedState = [...engineStateFilters, ...engineFilters]
    return {
      trigger: {
        event: ENGINE_EVENT_MAP[def.engineEvent],
        filters: dateFilters,
        stateFilters: combinedState.length ? combinedState : undefined,
        reenrollment: REENROLLMENT_MAP[data.reenrollment],
        filterLogic: 'or',
        stateFilterLogic: combinedState.length ? ((data.stateMatch ?? data.match) === 'any' ? 'or' : 'and') : undefined,
        // Always explicit: the engine reads ABSENT as require-a-match (`!== false`), so collapsing
        // false to undefined silently skipped every unmatched caller/form (caught live 08-24 when a
        // contact-less form never enrolled despite the toggle showing off).
        requireContactMatch: data.requireContactMatch === true,
      },
      gaps,
    }
  }

  // Each treatment moment is its own engine event; no moment yet falls back to the plan update.
  const milestone = data.treatment?.milestone
  const event =
    def.object === 'treatment_plan' && milestone
      ? TREATMENT_MOMENT_EVENTS[milestone].event
      : ENGINE_EVENT_MAP[def.engineEvent]
  return {
    trigger: {
      event,
      filters: engineFilters,
      stateFilters: engineStateFilters.length ? engineStateFilters : undefined,
      reenrollment: REENROLLMENT_MAP[data.reenrollment],
      filterLogic: data.match === 'any' ? 'or' : 'and',
      stateFilterLogic: stateFilters.length ? ((data.stateMatch ?? data.match) === 'any' ? 'or' : 'and') : undefined,
      // Always explicit — see the date-trigger branch above for why false must not collapse to absent.
      requireContactMatch: data.requireContactMatch === true,
    },
    gaps,
  }
}

/** True for the trigger kinds whose filters are a schedule rather than a change to match. */
export const isDateAnchored = (kind: TriggerKind): boolean => kind.endsWith('_date_reached')

/** A canvas offset → the engine's signed-offset string. The API's validator, probed on 08-19:
 *  the sign is required, the units are m/h/d/w/mo/y, the amount must be non-zero, and decimals are
 *  rejected. Zero being illegal is why "the morning of" is authored as hours before rather than 0d. */
export function encodeOffset(offset: { amount: number; unit: string; direction: 'before' | 'after' }): string {
  const amount = Math.max(1, Math.round(Math.abs(offset.amount)))
  return `${offset.direction === 'before' ? '-' : '+'}${amount}${offset.unit}`
}

const roundPos = (n: number): number => Math.round(n)

/** One canvas node → engine node input (+ gaps). Every node kind now maps; gaps are lossy-only.
 *  `locationHasBusinessHours` is the caller's answer to "can the chosen location actually hold a
 *  wait?" — only `true` suppresses the Wait caveat, so an unknown or unloaded location still warns
 *  rather than promising a hold nobody has verified. */
function serializeNode(
  node: WorkflowNode,
  locationHasBusinessHours?: boolean,
): { input?: WorkflowNodeInput; chained?: WorkflowNodeInput[]; gaps: WorkflowGap[] } {
  const gaps: WorkflowGap[] = []
  const base = { id: node.id, position: { x: roundPos(node.position.x), y: roundPos(node.position.y) } }
  const lossy = (message: string) => gaps.push({ nodeId: node.id, code: 'lossy_field', message })
  // `blocked` gates Activate (a step/condition the engine can't run as drawn); `lossy` only warns.
  const blocked = (message: string) => gaps.push({ nodeId: node.id, code: 'unsupported_node', message })

  // Variables render live as of 08-14 (TRK-5498, verified with a real SMS: `{{contact.firstName}}`
  // arrived as "Patrick"). What survives is the namespace trap: the engine resolves an UNKNOWN token
  // to an empty string, silently, so a bare `{{firstName}}` texts the patient "Hi ,". Our preview
  // can't show that (it fills either shape), which makes it exactly the kind of defect that only
  // surfaces on someone's phone. The picker always writes the namespaced form; this catches text
  // typed by hand or pasted from the old templates.
  const BARE_VARIABLE = new RegExp(`\\{\\{\\s*(${CONTACT_FIELD_KEYS.join(`|`)})\\s*\\}\\}`)
  /** A condition the engine will never match sends every contact down the wrong path in silence,
   *  so it blocks Activate rather than warning. See UNMATCHABLE_STEP_FIELDS. */
  const blockUnmatchableFields = (what: string, filters: WorkflowTriggerFilterInput[]) => {
    for (const filter of filters) {
      const label = UNMATCHABLE_STEP_FIELDS[filter.attribute]
      if (!label) continue
      blocked(
        `${what}: a contact has one name field, and ${label} is split out of it for display, so there is nothing to compare and every contact would take the "no" path. Use Full name instead.`,
      )
    }
  }

  const blockBareVariables = (what: string, ...texts: (string | undefined)[]) => {
    const hit = texts.find((text) => text && BARE_VARIABLE.test(text))
    if (!hit) return
    const name = BARE_VARIABLE.exec(hit!)![1]
    blocked(
      `${what} uses {{${name}}}, which the engine reads as an unknown variable and replaces with nothing. Write it as {{contact.${name}}}, or pick it from the variable list.`,
    )
  }

  switch (node.type) {
    case 'aiAgentSms': {
      blockBareVariables('The opening message', node.data.message)
      blockBareVariables('The brief', node.data.brief)
      // Engine names differ from the builder's; mapped here, as nightSend → quietHoursAction is.
      return {
        input: {
          ...base,
          type: WorkflowNodeType.InitiateAgent,
          toField: node.data.toField,
          message: node.data.message,
          brief: node.data.brief,
          busyBehavior: (node.data.whenBusy ?? 'skip') === 'takeOver' ? 'take_over' : 'do_nothing',
          // Only meaningful for do_nothing; the engine ignores it for take_over.
          busyLimitDays: node.data.skipIfAgentSpokeWithinDays,
        },
        gaps,
      }
    }
    case 'sendSms': {
      // Texting the number that called works: the engine reads callerPhone off the triggering call
      // even when the run has a matched contact (api #548), and the step is queued rather than
      // silently dropped (api #550 — the dedupId exceeded SQS's 128-char limit, and only call events
      // generate ids long enough to hit it). Verified end to end on 08-21: real inbound call, text
      // delivered to the caller. The note stays because "which record holds the caller's number" cost
      // a day: it is on the Call and on the Activity as phoneFromActivity, never on the contact.

      blockBareVariables('This message', node.data.message)
      // The engine resolves three kinds of recipient: a phone field off the CONTACT, `callerPhone`
      // off the triggering Call (api #548), and `phone` off the triggering Form (api #566,
      // TRK-5579). Anything else (e.g. the activity's `contactPhone`) reads `contact.<key>` =
      // undefined at run time and the step throws. Authorable, but blocks Activate.
      const RUNNABLE_TO_FIELDS = ['cellPhone', 'homePhone', 'workPhone', 'callerPhone', 'phone']
      if (node.data.toField && !RUNNABLE_TO_FIELDS.includes(node.data.toField)) {
        gaps.push({
          nodeId: node.id,
          code: 'unsupported_node',
          message: `The engine can text a contact's number, the caller's number, or the phone the form submitted — this field isn't readable at send time. Pick one of those.`,
        })
        return { input: { ...base, type: WorkflowNodeType.SendSms, message: node.data.message }, gaps }
      }
      if (node.data.toMode === 'custom') {
        // Texting a fixed number needs an engine `toNumber`; authorable but can't run yet → blocks Activate.
        gaps.push({
          nodeId: node.id,
          code: 'unsupported_node',
          message:
            'Texting a custom number needs a "toNumber" field on the engine. Flagged for backend (only a contact number runs today).',
        })
        return { input: { ...base, type: WorkflowNodeType.SendSms, message: node.data.message }, gaps }
      }
      return {
        input: {
          ...base,
          type: WorkflowNodeType.SendSms,
          message: node.data.message,
          toField: encodeToField(node.data.toField),
          // Night-send policy, live since api #587 (TRK-5587): the engine parks a quiet-hours send
          // until 8 AM ("hold") or drops it ("skip"). Always explicit — the requireContactMatch
          // lesson: absent and default-looking are not the same thing at the engine.
          quietHoursAction: node.data.nightSend ?? 'hold',
        },
        gaps,
      }
    }
    case 'initiateCall':
      // ringNumber = the staff/rep phone to ring and bridge to the contact (caller ID is the location
      // tracking number). Hunt-group / multi-ring is intentionally out of scope. whisperMessage is
      // the CONTACT-leg TTS opener (CallRail outbound_greeting_text) — two-phone verified 08-06:
      // the contact hears it on answer; staff get CallRail's built-in "Press 1" prompt. The engine
      // field name predates that finding.
      return {
        input: {
          ...base,
          type: WorkflowNodeType.InitiateCall,
          ringNumber: node.data.ringNumber,
          whisperMessage: node.data.whisper?.trim() ? node.data.whisper : undefined,
        },
        gaps,
      }
    case 'createTask': {
      blockBareVariables('This task', node.data.title, node.data.description)
      const assignee = assigneeInput(node.data.ownerId)
      return {
        input: {
          ...base,
          type: WorkflowNodeType.CreateTask,
          title: node.data.title,
          assigneeId: assignee.ownerId ?? undefined,
          roleId: assignee.roleId ?? undefined,
          dueInDays: node.data.dueType === 'relative' ? node.data.dueInDays : undefined,
          dueDate: node.data.dueType === 'absolute' ? node.data.dueDate : undefined,
          description: node.data.description?.trim() ? node.data.description : undefined,
        },
        gaps,
      }
    }
    case 'editTask': {
      blockBareVariables('This task edit', node.data.title, node.data.description)
      const assignee = node.data.ownerId === undefined ? undefined : assigneeInput(node.data.ownerId)
      return {
        input: {
          ...base,
          type: WorkflowNodeType.EditTask,
          onlyIfOpen: node.data.onlyIfOpen,
          status: node.data.status,
          archived: node.data.archived,
          // An empty assignee clears both, which is how the engine reads "Unassigned".
          assigneeId: assignee ? (assignee.ownerId ?? '') : undefined,
          roleId: assignee?.roleId ?? undefined,
          dueInDays: node.data.dueType === 'relative' ? node.data.dueInDays : undefined,
          dueDate: node.data.dueType === 'absolute' ? node.data.dueDate : undefined,
          title: node.data.title?.trim() ? node.data.title : undefined,
          description: node.data.description?.trim() ? node.data.description : undefined,
        },
        gaps,
      }
    }
    case 'tagObject':
      return {
        input: {
          ...base,
          type: node.data.operation === 'remove' ? WorkflowNodeType.RemoveTags : WorkflowNodeType.AddTags,
          tags: node.data.tags,
          target: node.data.target,
        },
        gaps,
      }
    case 'editObject': {
      if (node.data.target === 'activity') {
        lossy("Edit: the engine edits the contact only. Activity fields aren't editable yet.")
      }
      // One JSON object for the whole node: editContact.ts iterates the map and applies every entry
      // in a single contact save, so N rows cost the same one write as one row did.
      const rows = node.data.fields.filter((row) => row.field)
      const fields = rows.length
        ? JSON.stringify(
            Object.fromEntries(rows.map((row) => [row.field, row.operation === 'clear' ? null : row.value])),
          )
        : undefined
      return { input: { ...base, type: WorkflowNodeType.EditContact, fields }, gaps }
    }
    case 'addToPipeline':
      return {
        input: {
          ...base,
          type: WorkflowNodeType.SetPipelineStage,
          pipelineId: node.data.pipelineId,
          stage: node.data.stage,
        },
        gaps,
      }
    case 'assignOwner':
      return { input: { ...base, type: WorkflowNodeType.AssignOwner, userId: node.data.ownerId }, gaps }
    case 'addNote':
      blockBareVariables('This note', node.data.body)
      return { input: { ...base, type: WorkflowNodeType.CreateNote, content: node.data.body }, gaps }
    case 'logFollowUp':
      blockBareVariables('This follow-up', node.data.note)
      // No engine follow-up step yet: a note keeps the draft savable, and the gap blocks Activate.
      blocked('Logging a follow-up needs engine support (TRK-6138). Flagged for backend.')
      return { input: { ...base, type: WorkflowNodeType.CreateNote, content: node.data.note }, gaps }
    case 'sendPaperwork':
      blockBareVariables('This message', node.data.message)
      return {
        input: {
          ...base,
          type: WorkflowNodeType.SendPaperwork,
          paperworkTemplateId: node.data.paperworkTemplateId,
          expiresInDays: node.data.expiresInDays,
          message: node.data.message,
          toField: node.data.toField,
          quietHoursAction: node.data.nightSend ?? 'hold',
        },
        gaps,
      }
    case 'sendNotification': {
      blockBareVariables('This notification', node.data.message)
      const channel = node.data.channel === 'email' ? 'email' : node.data.channel === 'both' ? 'both' : 'app'
      return {
        input: {
          ...base,
          type: WorkflowNodeType.SendNotification,
          message: node.data.message,
          recipients: node.data.recipients,
          channel,
        },
        gaps,
      }
    }
    case 'wait':
      // The engine honours this (wait.ts shifts the resume time into the location's open hours), but
      // only when that location actually has open hours and a timezone saved. Without them it quietly
      // resumes at the raw time. Warn only while we can't see those hours: once the location has
      // them the hold genuinely works, and a caveat that never clears reads as a broken feature.
      if (node.data.businessHoursOnly && locationHasBusinessHours !== true) {
        lossy(
          'Wait: "only continue during business hours" needs opening hours and a time zone on the location. Without them the wait just continues at its normal time.',
        )
      }
      return {
        input: {
          ...base,
          type: WorkflowNodeType.Wait,
          duration: encodeDuration(node.data.duration, node.data.unit),
          // Always sent, never inferred: the toggle was drawn on the card but left out of the
          // input entirely, so "only continue during business hours" did nothing at the engine.
          businessHoursOnly: node.data.businessHoursOnly,
        },
        gaps,
      }
    case 'waitUntil': {
      // The engine reads only the run's contact here (TRK-6036), one flat group, so the panel offers
      // contact fields alone and a row from any other record would never match.
      const { filters, droppedEvent } = toFilters(node.data.conditions)
      if (droppedEvent) blocked(`Wait until: event conditions (e.g. "appointment booked") aren't usable here yet.`)
      blockUnmatchableFields('Wait until', filters)
      return {
        input: {
          ...base,
          type: WorkflowNodeType.Wait,
          duration: encodeDuration(node.data.duration, node.data.unit),
          businessHoursOnly: false,
          earlyExit: JSON.stringify({
            filters: toStepFilters(filters),
            filterLogic: node.data.match === 'any' ? 'or' : 'and',
          }),
        },
        gaps,
      }
    }
    case 'waitForReply':
    case 'waitForKeyword':
      return {
        input: {
          ...base,
          type: WorkflowNodeType.WaitForReply,
          timeout: node.data.within ? encodeDuration(node.data.within, node.data.unit ?? `days`) : undefined,
          // Absent means on, at both ends, so an older document keeps the engine's default.
          stopOnHumanTakeover: node.data.stopOnHumanTakeover,
          ...(node.type === 'waitForKeyword' ? { replyOptions: JSON.stringify(node.data.options) } : {}),
        },
        gaps,
      }
    case 'waitForCallResult':
      // Live since TRK-5395 closed (verified 08-12 with three real calls on one contact: answered,
      // voicemail-pickup-as-answered, and a clean no-answer, each resolved from the call THAT RUN
      // placed). Outcomes route correctly, so this no longer blocks. Worth knowing: resolution rides
      // the resume poller, so a branch lands within a few minutes rather than instantly.
      return { input: { ...base, type: WorkflowNodeType.WaitForCallResult }, gaps }
    case 'branch':
    case 'filter': {
      const what = node.type === 'branch' ? 'Branch' : 'Filter'
      const { filters, droppedEvent } = toFilters(node.data.conditions)
      if (droppedEvent) blocked(`${what}: event conditions (e.g. "appointment booked") aren't usable as filters yet.`)
      blockUnmatchableFields(what, filters)
      const checks = splitIntoChecks(node.data.conditions, node.data.match)
      const ids = chainNodeIds(node.id, checks.length)
      // A Branch's matched path is its "yes" condition; a Filter's single gate is "pass".
      const [input, ...chained] = checks.map((check, index) => ({
        ...base,
        id: ids[index],
        type: WorkflowNodeType.Branch,
        conditions: JSON.stringify([
          {
            id: node.type === 'branch' ? 'yes' : 'pass',
            ...(check.source === 'contact' ? {} : { source: check.source }),
            filters: toStepFilters(toFilters(check.conditions).filters).map((filter) =>
              filter.attribute === PAPERWORK_STATUS_FIELD ? { ...filter, attribute: 'status' } : filter,
            ),
            filterLogic: check.logic,
          },
        ]),
      }))
      return { input, chained, gaps }
    }
    case 'updateAppointment':
      return {
        input: { ...base, type: WorkflowNodeType.ConfirmAppointment, fields: JSON.stringify({ confirmed: true }) },
        gaps,
      }
    default:
      // trigger / exit are handled structurally before this switch is reached.
      return { gaps }
  }
}

/** Full canvas → CreateWorkflowInput with gap analysis. See module header for the gap contract. */
export function serializeWorkflow(args: {
  name: string
  /** The location this workflow belongs to. Required by the API since trackable-api #530: every send
   *  needs a location, and each run is bound to one. Empty here is not an error the serializer can
   *  fix, so it becomes a blocking gap rather than a silent save with a missing field. */
  locationId?: string
  /** When the trigger starts listening. The engine never enrolls on events older than this, so it
   *  doubles as the no-retroactive-enrollment guarantee (editable in the builder). */
  triggerStartDate?: string
  /** UI-only: the backfill switch, kept in builderJson so reopening shows it as it was left. */
  backfill?: boolean
  /** UI-only: whether this workflow has ever been live (see `WorkflowDoc.wentLive`). */
  wentLive?: boolean
  nodes: WorkflowNode[]
  edges: Edge[]
  settings?: WorkflowSettings
  /** Whether the chosen location has opening hours and a time zone saved, which is what the
   *  engine's business-hours hold needs (see `locationHasBusinessHours` in validation). Undefined
   *  means "not known yet" and is treated the same as no: the Wait caveat stays up. */
  locationHasBusinessHours?: boolean
  /** Gap messages that name the pipeline/list concept read this. Required, not defaulted: a caller
   *  with no component in scope (deploy templates, tests) must pass `listCopy(false)` explicitly,
   *  so the choice is auditable at the call site rather than a silent fallback in here. */
  copy: ListCopy
  /** Whether the chosen texting category still waits on an admin's save (`effectiveCategory`'s
   *  `pending`). A super's own save applies it, and a workflow the engine already holds as
   *  transactional has nothing left to apply, so neither carries the warning. */
  categoryPending?: boolean
}): SerializeResult {
  const {
    name,
    locationId,
    triggerStartDate,
    backfill,
    wentLive,
    nodes,
    edges,
    settings,
    locationHasBusinessHours,
    copy,
    categoryPending,
  } = args
  const gaps: WorkflowGap[] = []

  const triggerNode = nodes.find((node): node is Extract<WorkflowNode, { type: 'trigger' }> => node.type === 'trigger')
  if (!triggerNode) {
    return {
      blocked: true,
      gaps: [{ code: 'unsupported_node', message: 'A workflow needs a trigger before it can be created.' }],
    }
  }

  const { trigger, gaps: triggerGaps } = serializeTrigger(triggerNode, copy)
  gaps.push(...triggerGaps)

  // Structural nodes never reach the engine node list; their edges are pruned below.
  const droppedIds = new Set(nodes.filter((n) => n.type === 'trigger' || n.type === 'exit').map((n) => n.id))

  const nodeInputs: WorkflowNodeInput[] = []
  const chains = new Map<string, StepChain>()
  for (const node of nodes) {
    if (droppedIds.has(node.id)) continue
    const { input, chained, gaps: nodeGaps } = serializeNode(node, locationHasBusinessHours)
    gaps.push(...nodeGaps)
    if (input) nodeInputs.push(input)
    if (input && chained?.length && (node.type === 'branch' || node.type === 'filter')) {
      nodeInputs.push(...chained)
      chains.set(node.id, {
        ids: [input.id, ...chained.map((step) => step.id)],
        match: node.data.match,
        matchedHandle: node.type === 'branch' ? 'yes' : 'pass',
      })
    }
  }
  gaps.push(...pendingEngineGaps(nodes))
  if (triggerObject(triggerNode.data.kind) !== 'appointment') {
    for (const node of nodes) {
      if (node.type !== 'updateAppointment') continue
      gaps.push({
        nodeId: node.id,
        code: 'unsupported_node',
        message: `Confirm appointment needs an appointment trigger, like "Before or after an appointment", so it knows which appointment to confirm.`,
      })
    }
  }

  for (const node of nodes) {
    if (node.type !== 'waitForKeyword') continue
    const exits = new Set(forkHandles(node))
    if (!edges.some((edge) => edge.source === node.id && !exits.has(edge.sourceHandle ?? ''))) continue
    gaps.push({
      nodeId: node.id,
      code: 'unsupported_node',
      message: `Steps are attached to a reply option that no longer exists. Add the option back, or move those steps.`,
    })
  }

  const kindById = new Map(nodes.map((n) => [n.id, n.type]))
  // Keep an edge only when BOTH ends actually reached the engine payload. Structural nodes account
  // for most of what that prunes, but a node the serializer deliberately could not map (the AI agent
  // handoff, until TRK-5647) would otherwise leave an edge pointing at an id the payload has never
  // heard of. Such a node already blocks Activate, so the engine graph being incomplete costs
  // nothing, whereas a dangling reference is a save the API may simply refuse.
  const emittedIds = new Set(nodeInputs.map((input) => input.id))
  const encodedEdges: WorkflowEdgeInput[] = edges
    .filter((edge) => emittedIds.has(edge.source) && emittedIds.has(edge.target))
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      sourceHandle: encodeSourceHandle(kindById.get(edge.source), edge.sourceHandle),
      target: edge.target,
      targetHandle: edge.targetHandle ?? 'in',
    }))
  const edgeInputs = [
    ...encodedEdges.filter((edge) => !chains.has(edge.source)),
    ...[...chains].flatMap(([id, chain]) =>
      chainEdges(
        chain,
        encodedEdges.filter((edge) => edge.source === id),
      ),
    ),
  ]

  // Send-times enforcement is LIVE — verified on dev 2026-07-29 (TRK-5371): an out-of-window send
  // parked the run (`waitingFor: send_window`) and resumed at the window's next opening in the
  // contact's timezone. The old F3 blocking gap is retired.

  // Night sends stopped being lossy on 08-26: the engine holds a quiet-hours send until 8 AM by
  // default and skips only when the step says so (api #587, `quietHoursAction`). The old
  // lossy-window warnings are gone because their premise — refusal means the text is lost — is gone;
  // the per-step night-send control is where that intent lives now.
  const hasSmsStep = nodes.some((node) => node.type === 'sendSms')
  // The consent architecture that LANDED (api #588, 08-25) inverts the builder's category picker:
  // the engine defaults every workflow send to MARKETING (consent-gated), and only a super can mark
  // a workflow transactional via the ops-schema setWorkflowCategory — the workflow inputs still
  // don't accept a category (TRK-5580's open remainder). So a "Transactional" choice in the builder
  // is the one that can't reach the engine yet; a "Marketing" choice matches the engine default.
  if (hasSmsStep && categoryPending && settings?.category === 'transactional') {
    gaps.push({
      nodeId: triggerNode?.id,
      code: 'lossy_field',
      message: `Transactional texting is chosen, and applying it to the engine is an admin action — until an admin saves this workflow, the engine treats it as marketing and these texts only reach contacts who opted in.`,
    })
  }

  // builderJson round-trips the exact canvas (incl. UI-only fields the engine doesn't model), so
  // reopen is lossless. settings carry workflow-level config (quiet hours).
  const builderJson = JSON.stringify({
    version: 1,
    name,
    locationId,
    triggerStartDate,
    backfill,
    wentLive,
    nodes,
    edges,
    settings,
  })

  // Blocking, not advisory. The API's locationId is a String! that an empty string satisfies, so a
  // warning alone would let the builder create a workflow belonging to no location and report
  // success. Verified 08-19: that is exactly what happened before this returned `blocked`.
  if (!locationId) {
    gaps.push({
      // No hardcoded sentinel: an absent nodeId lets nodeIssues anchor this to the trigger, or to
      // the first node when the graph is headless — a 'workflow' id matches nothing and the error
      // used to vanish from the count the moment the trigger was deleted.
      nodeId: triggerNode?.id,
      code: 'unsupported_settings',
      message: `Choose which location this workflow runs for.`,
    })
    return { input: undefined, blocked: true, gaps }
  }

  return {
    input: {
      name,
      locationId,
      ...(triggerStartDate ? { triggerStartDate } : {}),
      trigger,
      nodes: nodeInputs,
      edges: edgeInputs,
      settings: settings ? JSON.stringify(settings) : undefined,
      builderJson,
    },
    blocked: false,
    gaps,
  }
}
